import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Container,
  Badge,
} from "react-bootstrap";
import { FiDownload, FiRefreshCw } from "react-icons/fi";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { format } from "date-fns";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import { getAssetDisposalReport } from "../../services/ReportServices";
import "react-datepicker/dist/react-datepicker.css";
import { BiSolidReport } from "react-icons/bi";

const periodOptions = [
  { label: "All", value: "all" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "7_days" },
  { label: "Last 15 Days", value: "15_days" },
  { label: "Last 30 Days", value: "30_days" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 3 Months", value: "3_months" },
  { label: "Custom", value: "custom" },
];


const missingStatusOptions = [
  { value: "", label: "All Status" },
  { value: "true", label: "Missing" },
  { value: "false", label: "Not Missing" },
];

const AssetDisposalReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[4]); // Default to "This Month"

  const [filters, setFilters] = useState({
    search: "", // ✅ renamed from query
    is_missing: "",
    period: "monthly",
    start_date: "",
    end_date: "",
    page: 1,
    page_size: 10,
  });

  useEffect(() => {
  const today = new Date();
  let start_date = "";
  let end_date = "";

  if (!selectedPeriod || selectedPeriod.value === "all") {
    setFilters((prev) => ({
      ...prev,
      period: "all",
      start_date: "",
      end_date: "",
      page: 1,
    }));
    return;
  }

  switch (selectedPeriod.value) {
    case "yesterday":
      start_date = end_date = format(
        new Date(today.setDate(today.getDate() - 1)),
        "yyyy-MM-dd"
      );
      break;
    case "7_days":
      start_date = format(
        new Date(today.setDate(today.getDate() - 6)),
        "yyyy-MM-dd"
      );
      end_date = format(new Date(), "yyyy-MM-dd");
      break;
    case "15_days":
      start_date = format(
        new Date(today.setDate(today.getDate() - 14)),
        "yyyy-MM-dd"
      );
      end_date = format(new Date(), "yyyy-MM-dd");
      break;
    case "30_days":
      start_date = format(
        new Date(today.setDate(today.getDate() - 29)),
        "yyyy-MM-dd"
      );
      end_date = format(new Date(), "yyyy-MM-dd");
      break;
    case "last_month":
      const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDayLastMonth = new Date(firstDayThisMonth - 1);
      start_date = format(new Date(lastDayLastMonth.getFullYear(), lastDayLastMonth.getMonth(), 1), "yyyy-MM-dd");
      end_date = format(lastDayLastMonth, "yyyy-MM-dd");
      break;
    case "3_months":
      start_date = format(
        new Date(today.getFullYear(), today.getMonth() - 2, 1),
        "yyyy-MM-dd"
      );
      end_date = format(new Date(), "yyyy-MM-dd");
      break;
    case "weekly":
      start_date = format(
        new Date(today.setDate(today.getDate() - today.getDay() + 1)),
        "yyyy-MM-dd"
      );
      end_date = format(new Date(), "yyyy-MM-dd");
      break;
    case "monthly":
      start_date = format(new Date(today.getFullYear(), today.getMonth(), 1), "yyyy-MM-dd");
      end_date = format(new Date(), "yyyy-MM-dd");
      break;
    case "yearly":
      start_date = format(new Date(today.getFullYear(), 0, 1), "yyyy-MM-dd");
      end_date = format(new Date(), "yyyy-MM-dd");
      break;
    case "custom":
      start_date = "";
      end_date = "";
      break;
    default:
      start_date = "";
      end_date = "";
  }

  setFilters((prev) => ({
    ...prev,
    period: selectedPeriod.value,
    start_date,
    end_date,
    page: 1,
  }));
}, [selectedPeriod]);


  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, page: 1 }));
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (
        filters.period === "custom" &&
        (!filters.start_date || !filters.end_date)
      ) {
        setReportData([]);
        setTotalCount(0);
        return;
      }

      const params = {
        search: filters.search || undefined,
        is_missing: filters.is_missing || undefined,
        period: filters.period,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        page: filters.page,
        page_size: filters.page_size,
      };

      const response = await getAssetDisposalReport(params);
      if (response?.data?.results) {
        setReportData(response.data.results);
        setTotalCount(response.data.count || 0);
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (err) {
      console.error(err);
      const message =
        err.response?.data?.error || err.message || "Failed to load report";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const exportParams = {
        ...filters,
        export: true,
      };

      const response = await getAssetDisposalReport(exportParams, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "asset_disposal_report.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Export started successfully");
    } catch (error) {
      toast.error("Export failed. Please try again.");
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (selectedPage) => {
    setFilters((prev) => ({ ...prev, page: selectedPage.selected + 1 }));
    window.scrollTo(0, 0);
  };

  const getMissingStatusBadge = (isMissing) => {
    return isMissing ? (
      <Badge bg="danger">Missing</Badge>
    ) : (
      <Badge bg="success">Not Missing</Badge>
    );
  };

  useEffect(() => {
    const delay = setTimeout(fetchData, 300);
    return () => clearTimeout(delay);
  }, [
    filters.page,
    filters.page_size,
    filters.search, // ✅ updated
    filters.is_missing,
    filters.start_date,
    filters.end_date,
  ]);

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="text-primary mb-0">Asset Disposal Report</h2>
          <ol className="breadcrumb breadcrumb-arrow mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Asset Disposal Report</li>
          </ol>
        </div>
        <div
          className="d-flex align-items-center gap-2 ms-auto mt-2 mt-md-0"
          style={{ zIndex: 10 }}
        >
          <div style={{ width: "200px" }}>
            <Reportselect />
          </div>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exportLoading || loading}
            style={{ height: "41px" }}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FiDownload className="me-1" />
            )}
            Export
          </Button>
        </div>
      </div>

      <Card style={{ minHeight: "350px" }}>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 text-primary fw-bold fs-5 mb-0">
            <BiSolidReport size={22} color="#255d5c" />
            <h5 className="mb-0">Asset Disposal Report</h5>
          </div>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <DebounceSmallSearchInput
              value={filters.search}
              handleSearchChange={(value) =>
                handleFilterChange("search", value)
              }
              placeholder="Search ..."
              debounceTime={500}
            />

            <GlobalSearchableDropdown
              options={missingStatusOptions}
              selected={missingStatusOptions.find(
                (opt) => opt.value === filters.is_missing
              )}
              onSelect={(selected) =>
                handleFilterChange("is_missing", selected?.value || "")
              }
              placeholder="Missing Status"
              width="150px"
            />

            <GlobalSearchableDropdown
              options={periodOptions}
              selected={selectedPeriod}
              onSelect={setSelectedPeriod}
              placeholder="Period"
              width="150px"
            />

            {selectedPeriod?.value === "custom" && (
              <div style={{ width: "260px" }}>
                <DatePicker
                  selectsRange
                  startDate={
                    filters.start_date ? new Date(filters.start_date) : null
                  }
                  endDate={filters.end_date ? new Date(filters.end_date) : null}
                  onChange={([start, end]) => {
                    setFilters((prev) => ({
                      ...prev,
                      start_date: start ? format(start, "yyyy-MM-dd") : "",
                      end_date: end ? format(end, "yyyy-MM-dd") : "",
                      period: "custom",
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                />
              </div>
            )}

            
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger">
              {error}
              <Button
                variant="link"
                size="sm"
                onClick={fetchData}
                className="p-0 ms-2"
              >
                <FiRefreshCw /> Try again
              </Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading report data...</p>
            </div>
          ) : (
            <Row>
              <Col lg={12}>
                <Card className="h-100">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Asset Number</th>
                            <th>Asset Name</th>
                            <th>Disposed By</th>
                            <th>Disposal Date</th>
                            <th>Reason</th>
                            <th>Status</th>
                            <th>Original Cost</th>
                            <th>Current Value</th>
                            <th>Location</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center py-4">
                                No data available
                              </td>
                            </tr>
                          ) : (
                            reportData.map((item, index) => (
                              <tr key={`${item.asset_number}-${index}`}>
                                <td>{item.asset_number || "-"}</td>
                                <td>{item.asset_name || "-"}</td>
                                <td>{item.disposed_by || "-"}</td>
                                <td>{item.disposal_date || "-"}</td>
                                <td>{item.reason || "-"}</td>
                                <td>
                                  {getMissingStatusBadge(item.is_missing)}
                                </td>
                                <td>{item.original_cost || "-"}</td>
                                <td>{item.current_value || "-"}</td>
                                <td>{item.location || "-"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                    {totalCount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                        <div>
                          Showing {(filters.page - 1) * filters.page_size + 1}{" "}
                          to{" "}
                          {Math.min(
                            filters.page * filters.page_size,
                            totalCount
                          )}{" "}
                          of {totalCount} entries
                        </div>
                        <Pagination
                          currentPage={filters.page - 0}
                          recordsPage={filters.page_size}
                          dataLength={totalCount}
                          handlePagination={handlePageChange}
                          showPageInfo={false}
                        />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AssetDisposalReport;
